import { takeLatest, takeEvery, take } from 'redux-saga/effects';
import * as Debug from '@hlibs/debug';

export default class ReduxSaga
{    
    static create = (name, actionSagas = {}) => new ReduxSaga(name, actionSagas);

    static actionCreator = {
        takeLatest: (sagaFunc = (payload) => {}, reducer = (state) => state) => { return { sagaFunc, type: 'takeLatest', reducer } },
        takeEvery: (sagaFunc = (payload) => {}, reducer = (state) => state) => { return { sagaFunc, type: 'takeEvery', reducer } },
        take: (sagaFunc = (payload) => {}, reducer = (state) => state) => { return { sagaFunc, type: 'take', reducer } },
    };

    dispatchAction(action, payload)
    {
        if(!this.#_store)
        {
            Debug.error(this.constructor.name + '.setStore 로 store 를 지정해주세요.');
        }

        const actionSaga = this.#_actionSagas[action];

        if(!actionSaga) return;

        this.#_store.dispatch(actionSaga.action(payload));
    };

    // set state
    set(data, key = 'data')
    {
        if(!this.#_store)
        {
            Debug.error(this.constructor.name + '.setStore 로 store 를 지정해주세요.');
        }

        this.#_store.dispatch(this.#_actions.Set(data, key));
    };

    // set state multiple => object type 으로 전달
    setMultiple(dataObject)
    {
        if(!this.#_store)
        {
            Debug.error(this.constructor.name + '.setStore 로 store 를 지정해주세요.');
        }

        this.#_store.dispatch(this.#_actions.SetMultiple(dataObject));
    };

    get(key)
    {
        const state = this.getState();

        if(!key) return state;

        return state && state[key] ? state[key] : null;
    }

    getState(key = null) 
    { 
        return this.get(key);
    };

    // states 초기화
    reset(additionalInitialState = {}, preserveKeys = [])
    {
        this.#_store.dispatch(this.#_actions.Reset(additionalInitialState, preserveKeys));
    }

    dispatch(...args)
    {
        if(this.#_store)
        {
            this.#_store.dispatch(...args);
        }

        else
        {
            Debug.error(this.constructor.name + '.setStore 로 store 를 지정해주세요.');
        }
    };

    constructor(name, actionSagas = {})
    {
        if (typeof name !== 'string') 
        {
            Debug.error('name must be a string');
        }    
    
        const _this = this;

        const nameUpperCase = name.toUpperCase();

        const initialState = { payload: null, funcAsync: null, processing: false, data: null, error: null, extra: null };
        
        const actionTypes = {
            Set: nameUpperCase + '/SET',
            SetMultiple: nameUpperCase + '/SET_MULTIPLE',
            Reset: nameUpperCase + '/RESET',
        };    

        const actions = {
            Set: (data, key) => { return { type: actionTypes.Set, data, key }; },
            SetMultiple: (dataObject) => { return { type: actionTypes.SetMultiple, dataObject }; },
            Reset: (additionalInitialState = {}, preserveKeys = []) => { return { type: actionTypes.Reset, additionalInitialState, preserveKeys }; },
        };

        const reducer = (state = initialState, action) => 
        {
            if(this.#_actionSagas && Object.keys(this.#_actionSagas).length > 0)
            {
                for(let keyCur in this.#_actionSagas)
                {
                    const actionSagaCur = this.#_actionSagas[keyCur];

                    if(actionSagaCur.action == action) return actionSagaCur.reducer(state);
                }
            }

            switch (action.type) 
            {
                case actionTypes.Set:
                    return action.key ? {
                        ...state,
                        [action.key] : action.data
                    } : {
                        ...state,
                        ...action.data
                    };
                case actionTypes.SetMultiple:
                    return {
                        ...state,
                        ...action.dataObject
                    };
                case actionTypes.Reset:
                    const newState = { ...initialState };

                    if(action.preserveKeys && Array.isArray(action.preserveKeys))
                    {
                        for(let keyCur of action.preserveKeys)
                        {
                            newState[keyCur] = state[keyCur];
                        }
                    }
                    if(action.additionalInitialState && typeof action.additionalInitialState == 'object')
                    {
                        for(let keyCur in action.additionalInitialState)
                        {
                            newState[keyCur] = action.additionalInitialState[keyCur];
                        }
                    }
                    return newState;
                default:
                    return state;                    
            };
        };

        if(actionSagas && Object.keys(actionSagas).length > 0)
        {
            for(let keyCur in actionSagas)
            {
                const actionSagaCur = actionSagas[keyCur];

                const actionNameCur = nameUpperCase + '/' + actionSagaCur.name.toUpperCase();;

                actionSagaCur.action = actionNameCur;

                actionTypes[keyCur] = actionNameCur;

                actions[keyCur] = (payload) => { return { type: actionNameCur, payload }; }    
            }    
        }

        const saga = function* watcher()
        {
            if(this.#_actionSagas && Object.keys(this.#_actionSagas).length > 0)
            {
                for(let keyCur in this.#_actionSagas)
                {
                    const actionSagaCur = this.#_actionSagas[keyCur];

                    switch(actionSagaCur.type)
                    {
                        case 'takeEvery': yield takeEvery(actionSagaCur.action, actionSagaCur.sagaFunc); break;
                        case 'take': yield take(actionSagaCur.action, actionSagaCur.sagaFunc); break;
                        default: yield takeLatest(actionSagaCur.action, actionSagaCur.sagaFunc); break;
                    }
                }
            }
        };

        this.#_name = name;
        this.#_actionTypes = actionTypes;
        this.#_actions = actions;
        this.#_reducer = reducer;
        this.#_saga = saga;
        this.#_actionSagas = actionSagas;

        return this;
    }

    setStore(store) { this.#_store = store; };

    #_store = null;
    #_name = "";

    #_actions = null;
    #_reducer = null;    
    #_actionTypes = null;
    #_saga = null;

    #_actionSagas = {};

    get name() { return this.#_name; };
    get actions() { return this.#_actions; };
    get actionTypes() { return this.#_actionTypes; };
    get reducer() { return this.#_reducer; };
    get saga() { return this.#_saga; };
    get store() { return this.#_store; };
};